/* eslint-disable: max-classes-per-file */
import { DataSeries } from '@next/core-lib/models';
import { createAction, props } from '@ngrx/store';

import {
  BenchmarkData,
  BenchmarkSettings,
  BenchmarkTypes,
  DataViewType,
  FlockDataParameter,
  GetBenchmarksRequest,
  GetFlockDataRequest,
  GlobalFilterSettings,
  KpiGroup as KpiGroup,
  LocalFilterSettings,
  NormalizedTimeSeries,
  QueryableFlockData,
  ViewMode,
} from '../../models/farmer-insights.model';

export const loadTimeSeriesRequest = createAction(
  '[FarmerInsights] Load TimeSeries request',
  props<{ getFlockDataRequest: GetFlockDataRequest }>(),
);

export const refreshTimeSeries = createAction('[FarmerInsights] Refresh TimeSeries');

export const loadTimeSeriesSuccess = createAction(
  '[FarmerInsights] Load TimeSeries success',
  props<{ timeSeries: DataSeries<number> }>(),
);

export const loadQueryableFlockDataRequest = createAction(
  '[FarmerInsights] Load QueryableFlockData request',
);

export const loadQueryableFlockDataSuccess = createAction(
  '[FarmerInsights] Load QueryableFlockData success',
  props<{
    queryableFlockData: QueryableFlockData;
    localFilterSettings: LocalFilterSettings;
    globalFilterSettings: GlobalFilterSettings;
  }>(),
);

export const loadkpiGroups = createAction(
  '[FarmerInsights] Load KpiGroupings',
  props<{
    kpiGroups: KpiGroup[];
  }>(),
);

export const loadQueryableFlockDataFailed = createAction(
  '[FarmerInsights] Load QueryableFlockData failed',
  props<{ error: string }>(),
);

export const convertToNormalizedTimeSeriesArray = createAction(
  '[FarmerInsights] Convert from TimeSeries to NormalizedTimeSeries[]',
  props<{ timeSeries: DataSeries<number> }>(),
);

export const setNormalizedTimeSeriesArray = createAction(
  '[FarmerInsights] Set NormalizedTimeSeriesArray',
  props<{ normalizedTimeSeriesArray: NormalizedTimeSeries[] }>(),
);

export const loadTimeSeriesFailed = createAction(
  '[FarmerInsights] Load TimeSeries failed',
  props<{ error: string }>(),
);

export const setLocationsFilter = createAction(
  '[FarmerInsights] Set Locations Filter',
  props<{ locationIds: number[] }>(),
);

export const setDateRangeFilter = createAction(
  '[FarmerInsights] Set DateRange Filter',
  props<{ startDate: string; endDate: string; description?: string }>(),
);

export const setCyclesFilter = createAction(
  '[FarmerInsights] Set Cycles Filter',
  props<{ cycleIds: number[] }>(),
);

export const setBarnsFilter = createAction(
  '[FarmerInsights] Set Barns Filter',
  props<{ barnIds: number[] }>(),
);

export const setSlideInFilters = createAction(
  '[FarmerInsights] Set All Filters on Slide-in',
  props<{ locationIds: number[]; cycleIds: number[]; barnIds: number[] }>(),
);

export const setDataFilters = createAction(
  '[FarmerInsights] Set Data Filters',
  props<{
    locationIds: number[];
    cycleIds: number[];
    barnIds: number[];
    KPIs: FlockDataParameter[];
  }>(),
);

export const setKpisFilter = createAction(
  '[FarmerInsights] Set KPIs Filter',
  props<{ KPIs: FlockDataParameter[] }>(),
);

export const setDataViewTypeFilter = createAction(
  '[FarmerInsights] Set DataViewType Filter',
  props<{ dataViewType: DataViewType }>(),
);

export const setSelectedViewMode = createAction(
  '[FarmerInsights] Set selected ViewMode Filter',
  props<{ viewMode: ViewMode }>(),
);

export const setSelectedConceptId = createAction(
  '[FarmerInsights] Set selected ConceptId',
  props<{ conceptId: number }>(),
);

export const setDetailedView = createAction(
  '[FarmerInsights] Set Detailed View',
  props<{ isDetailedView: boolean }>(),
);

export const loadBenchmarkSelectedPeriodRequest = createAction(
  '[FarmerInsights] Load benchmark Selected Period request',
  props<{ getBenchmarksSelectedPeriodRequest: GetBenchmarksRequest }>(),
);
export const loadBenchmarkLastWeeksRequest = createAction(
  '[FarmerInsights] Load benchmark Last Weeks request',
  props<{ getBenchmarksLastWeeksRequest: GetBenchmarksRequest }>(),
);
export const loadBenchmarkCustomerAverageSelectedPeriodRequest = createAction(
  '[FarmerInsights] Load benchmark CustomerAverageSelectedPeriod request',
  props<{ getBenchmarkCustomerAverageSelectedPeriodRequest: GetBenchmarksRequest }>(),
);

export const refreshBenchmarkTimeSeries = createAction(
  '[FarmerInsights] Refresh benchmark TimeSeries',
);

export const loadBenchmarkTimeSeriesSuccess = createAction(
  '[FarmerInsights] Load benchmark TimeSeries success',
  props<{ timeSeries: DataSeries<number> }>(),
);
export const loadBenchmarkLastWeeksSuccess = createAction(
  '[FarmerInsights] Load benchmark Last Weeks success',
  props<{ timeSeries: DataSeries<number> }>(),
);
export const loadBenchmarkCustomerAverageSelectedPeriodSuccess = createAction(
  '[FarmerInsights] Load benchmark CustomerAverageSelectedPeriod success',
  props<{ timeSeries: DataSeries<number> }>(),
);

export const loadBenchmarkTimeSeriesFailed = createAction(
  '[FarmerInsights] Load benchmark TimeSeries failed',
  props<{ error: string }>(),
);

export const convertToNormalizedBenchmarkData = createAction(
  '[FarmerInsights] Convert from TimeSeries to BenchmarkData',
  props<{ benchmarkType: BenchmarkTypes; timeSeries: DataSeries<number> }>(),
);

export const setNormalizedBenchmarkSelectedPeriodData = createAction(
  '[FarmerInsights] Set Normalized BenchmarkData for selected period',
  props<{ normalizedBenchmarkData: BenchmarkData }>(),
);
export const setNormalizedBenchmarkLastWeeksData = createAction(
  '[FarmerInsights] Set Normalized BenchmarkData for last weeks',
  props<{ normalizedBenchmarkData: BenchmarkData }>(),
);
export const setNormalizedBenchmarkCustomerAverageSelectedPeriodData = createAction(
  '[FarmerInsights] Set Normalized BenchmarkData for customer average for selected period',
  props<{ normalizedBenchmarkData: BenchmarkData }>(),
);

export const loadData = createAction('[FarmerInsights] Load data');
export const clearFarmerInsightsState = createAction('[FarmerInsights] Clear State');

export const loadFarmerInsightsBenchmarkSettings = createAction(
  '[FarmerInsights] Load benchmark settings',
);
export const setFarmerInsightsBenchmarkSettings = createAction(
  '[FarmerInsights] Set benchmark settings',
  props<{ settings: BenchmarkSettings; save: boolean }>(),
);

export const loadFarmerInsightsLocalFilterSettingsRequest = createAction(
  '[FarmerInsights] Load local filter settings request',
);

export const loadFarmerInsightsLocalFilterSettingsSuccess = createAction(
  '[FarmerInsights] Load local filter settings success',
  props<{ localFilterSettings: LocalFilterSettings }>(),
);

export const loadFarmerInsightsGlobalFilterSettingsRequest = createAction(
  '[FarmerInsights] Load global filter settings request',
);

export const loadFarmerInsightsGlobalFilterSettingsSuccess = createAction(
  '[FarmerInsights] Load global filter settings success',
  props<{ globalFilterSettings: GlobalFilterSettings }>(),
);

export const setFarmerInsightsGlobalFilterSettings = createAction(
  '[FarmerInsights] Set global filter settings',
  props<{ globalFilterSettings: GlobalFilterSettings }>(),
);

export const setFarmerInsightsLocalFilterSettings = createAction(
  '[FarmerInsights] Set local filter settings',
  props<{ localFilterSettings: LocalFilterSettings }>(),
);

export const resetFarmerInsightsLocalFilterSettings = createAction(
  '[FarmerInsights] Reset local filter settings',
);

export const resetFarmerInsightsGlobalFilterSettings = createAction(
  '[FarmerInsights] Reset global filter settings',
);
